/**
 * UnifiedQueuePatient - Patient card for unified queue
 *
 * Handles QR code check-ins, targeted remote invites (full CMR snapshot), and SMS-only check-ins.
 */

import React, { useState } from 'react';
import {
  Box,
  Paper,
  Typography,
  Chip,
  IconButton,
  Button,
  Tooltip,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Collapse,
  Divider,
  List,
  ListItem,
  ListItemText,
  Alert,
} from '@mui/material';
import {
  QrCode2,
  Sms,
  NotificationsActive,
  CheckCircle,
  AccessTime,
  ExpandMore,
  ExpandLess,
  Phone,
  Delete,
  MedicalServices,
  WarningAmber,
  Person,
  Visibility,
  History,
  Info,
  Edit,
  PlayArrow,
  LocalHospital,
  ExitToApp,
  DoNotDisturb,
  PhonelinkRing,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import { alpha } from '@mui/material/styles';
import { format, formatDistanceToNow } from 'date-fns';
import { PatientBanner } from '@healthdesk/shared-components';
import { tokens } from '@healthdesk/shared-utils';

/**
 * Get status chip based on visit status
 */
const StatusChip = ({ status, calledAt, visit, onResumeSession }) => {
  const getChipProps = () => {
    switch (status) {
      case 'called':
        return {
          label: 'Called',
          color: 'warning',
          icon: <NotificationsActive sx={{ fontSize: '1rem' }} />,
        };
      case 'in_progress':
        return {
          label: 'In Progress',
          color: 'info',
          icon: <AccessTime sx={{ fontSize: '1rem' }} />,
        };
      case 'completed':
        return {
          label: 'Completed',
          color: 'success',
          icon: <CheckCircle sx={{ fontSize: '1rem' }} />,
        };
      case 'admitted':
        return {
          label: 'Admitted',
          color: 'error',
          icon: <LocalHospital sx={{ fontSize: '1rem' }} />,
        };
      case 'discharged':
        return {
          label: 'Discharged',
          color: 'success',
          icon: <ExitToApp sx={{ fontSize: '1rem' }} />,
        };
      case 'declined':
        return {
          label: 'Declined',
          sx: { bgcolor: tokens.colors.msiRed.light, color: 'common.white' },
          icon: <DoNotDisturb sx={{ fontSize: '1rem', color: 'common.white' }} />,
        };
      default:
        return {
          label: 'Waiting',
          color: 'primary',
          variant: 'outlined',
        };
    }
  };

  const { sx: chipSx, ...chipProps } = getChipProps();
  
  // Make "In Progress" chip clickable if there's a linked draft
  const isClickable = status === 'in_progress' && visit?.chartmindDraftId && onResumeSession;

  const mergedSx = {
    ...chipSx,
    ...(isClickable && { 
      cursor: 'pointer', 
      '&:hover': { opacity: 0.8, boxShadow: 1 } 
    }),
  };
  
  return (
    <Stack direction="row" spacing={1} alignItems="center">
      <Chip 
        size="small" 
        {...chipProps}
        onClick={isClickable ? () => onResumeSession(visit) : undefined}
        sx={Object.keys(mergedSx).length > 0 ? mergedSx : undefined}
        title={isClickable ? 'Click to resume ChartMind session' : undefined}
      />
      {status === 'called' && calledAt && (() => {
        const calledDate =
          calledAt instanceof Date ? calledAt : calledAt?.toDate?.() || new Date(calledAt);
        return (
          <Typography
            variant="caption"
            color="text.secondary"
            component="span"
            title={calledDate.toISOString()}
          >
            {formatDistanceToNow(calledDate, { addSuffix: true })}
          </Typography>
        );
      })()}
    </Stack>
  );
};

/**
 * CMR Summary for QR / targeted (form snapshot) check-ins
 */
const CMRSummary = ({ cmrSnapshot, customFieldResponses }) => {
  const medications = cmrSnapshot?.['medications.all'] || cmrSnapshot?.medications || [];
  const allergies = cmrSnapshot?.['allergies.all'] || cmrSnapshot?.allergies || [];
  const reasonForVisit = customFieldResponses?.['reason-for-visit'] || customFieldResponses?.reason;

  return (
    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap', mt: 1 }}>
      {reasonForVisit && (
        <Chip
          size="small"
          label={reasonForVisit}
          color="primary"
          variant="outlined"
        />
      )}
      <Chip
        size="small"
        icon={<MedicalServices sx={{ fontSize: '0.9rem' }} />}
        label={`${Array.isArray(medications) ? medications.length : 0} meds`}
        variant="outlined"
      />
      <Chip
        size="small"
        icon={<WarningAmber sx={{ fontSize: '0.9rem' }} />}
        label={`${Array.isArray(allergies) ? allergies.length : 0} allergies`}
        variant="outlined"
        color={Array.isArray(allergies) && allergies.length > 0 ? 'warning' : 'default'}
      />
    </Box>
  );
};

/**
 * Format date for display (no Date parsing to avoid timezone issues)
 */
const formatDateDisplay = (value) => {
  if (!value) return 'N/A';
  
  const monthNames = [
    'January', 'February', 'March', 'April', 'May', 'June',
    'July', 'August', 'September', 'October', 'November', 'December'
  ];
  
  // Handle YYYY-MM-DD format
  const isoMatch = String(value).match(/^(\d{4})-(\d{2})-(\d{2})$/);
  if (isoMatch) {
    const [, year, month, day] = isoMatch;
    const monthIndex = parseInt(month, 10) - 1;
    const dayNum = parseInt(day, 10);
    return `${monthNames[monthIndex]} ${dayNum}, ${year}`;
  }
  
  // Handle M/D/YYYY or MM/DD/YYYY format
  const mdyMatch = String(value).match(/^(\d{1,2})\/(\d{1,2})\/(\d{4})$/);
  if (mdyMatch) {
    const [, month, day, year] = mdyMatch;
    const monthIndex = parseInt(month, 10) - 1;
    const dayNum = parseInt(day, 10);
    return `${monthNames[monthIndex]} ${dayNum}, ${year}`;
  }
  
  // Return as-is if format not recognized
  return value;
};

/**
 * Full CMR Dialog
 */
const CMRDialog = ({ open, onClose, visit }) => {
  const cmr = visit?.cmrSnapshot || {};
  const customFields = visit?.customFieldResponses || {};
  const akaNames = visit?.akaNames || [];
  const previousDOBs = visit?.previousDOBs || [];
  const dataModifiedDuringCheckIn = visit?.dataModifiedDuringCheckIn || false;
  const modifiedFields = visit?.modifiedFields || [];

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>
        Patient CMR - {cmr?.['patient.fullName'] || cmr?.patient?.fullName || 'Unknown'}
        {dataModifiedDuringCheckIn && (
          <Chip 
            icon={<Edit sx={{ fontSize: '0.9rem' }} />}
            label="Updated at check-in" 
            size="small" 
            color="info"
            sx={{ ml: 1, verticalAlign: 'middle' }}
          />
        )}
      </DialogTitle>
      <DialogContent dividers>
        {/* Patient Info */}
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Demographics
        </Typography>
        <List dense>
          <ListItem>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>Name</span>
                  {modifiedFields.includes('patient.firstName') || modifiedFields.includes('patient.lastName') ? (
                    <Chip label="Updated" size="small" color="info" sx={{ height: 16, fontSize: '0.65rem' }} />
                  ) : null}
                </Box>
              }
              secondary={cmr?.['patient.fullName'] || `${cmr?.['patient.firstName'] || ''} ${cmr?.['patient.lastName'] || ''}`.trim() || 'N/A'} 
            />
          </ListItem>
          
          {/* AKA Names Section */}
          {akaNames.length > 0 && (
            <ListItem sx={{ pl: 4, bgcolor: 'grey.50' }}>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <History sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Also Known As (AKA)
                    </Typography>
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {akaNames.map((aka, i) => (
                      <Chip 
                        key={i} 
                        label={`${aka.firstName} ${aka.lastName}`}
                        size="small"
                        variant="outlined"
                        color="info"
                        sx={{ height: 22 }}
                      />
                    ))}
                  </Box>
                }
              />
            </ListItem>
          )}

          <ListItem>
            <ListItemText 
              primary={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span>Date of Birth</span>
                  {modifiedFields.includes('patient.dateOfBirth') ? (
                    <Chip label="Updated" size="small" color="info" sx={{ height: 16, fontSize: '0.65rem' }} />
                  ) : null}
                </Box>
              }
              secondary={formatDateDisplay(cmr?.['patient.dateOfBirth'])} 
            />
          </ListItem>
          
          {/* Previous DOBs Section */}
          {previousDOBs.length > 0 && (
            <ListItem sx={{ pl: 4, bgcolor: 'grey.50' }}>
              <ListItemText 
                primary={
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <History sx={{ fontSize: '1rem', color: 'text.secondary' }} />
                    <Typography variant="caption" color="text.secondary">
                      Previous Dates of Birth
                    </Typography>
                    <Tooltip title="Patient has updated their date of birth. Review for identity verification.">
                      <Info sx={{ fontSize: '0.9rem', color: 'warning.main', cursor: 'help' }} />
                    </Tooltip>
                  </Box>
                }
                secondary={
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mt: 0.5 }}>
                    {previousDOBs.map((dob, i) => (
                      <Chip 
                        key={i} 
                        label={formatDateDisplay(dob)}
                        size="small"
                        variant="outlined"
                        color="warning"
                        sx={{ height: 22 }}
                      />
                    ))}
                  </Box>
                }
              />
            </ListItem>
          )}

          <ListItem>
            <ListItemText 
              primary="Phone" 
              secondary={cmr?.['patient.phone'] || 'N/A'} 
            />
          </ListItem>
        </List>

        <Divider sx={{ my: 2 }} />

        {/* Medications */}
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Medications
        </Typography>
        {cmr?.['medications.all'] && Array.isArray(cmr['medications.all']) && cmr['medications.all'].length > 0 ? (
          <List dense>
            {cmr['medications.all'].map((med, i) => (
              <ListItem key={i}>
                <ListItemText primary={typeof med === 'string' ? med : med.name || JSON.stringify(med)} />
              </ListItem>
            ))}
          </List>
        ) : (
          <Typography variant="body2" color="text.secondary">No medications listed</Typography>
        )}

        <Divider sx={{ my: 2 }} />

        {/* Allergies */}
        <Typography variant="subtitle2" color="primary" gutterBottom>
          Allergies
        </Typography>
        {cmr?.['allergies.all'] && Array.isArray(cmr['allergies.all']) && cmr['allergies.all'].length > 0 ? (
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {cmr['allergies.all'].map((allergy, i) => (
              <Chip 
                key={i} 
                label={typeof allergy === 'string' ? allergy : allergy.name || JSON.stringify(allergy)} 
                color="warning" 
                size="small" 
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary">No known allergies</Typography>
        )}

        {/* Custom Fields */}
        {Object.keys(customFields).length > 0 && (
          <>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" color="primary" gutterBottom>
              Check-in Responses
            </Typography>
            <List dense>
              {Object.entries(customFields).map(([key, value]) => (
                <ListItem key={key}>
                  <ListItemText 
                    primary={key.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase())} 
                    secondary={String(value)} 
                  />
                </ListItem>
              ))}
            </List>
          </>
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
};

/**
 * Main UnifiedQueuePatient component
 */
const UnifiedQueuePatient = ({ 
  visit, 
  clinicName,
  showClinicName = false,
  onNotify, 
  onUpdateStatus, 
  onDelete,
  onDecline,
  onSelectPatient,
  onResumeSession,
  notifyLoading,
  updateLoading,
  deleteLoading,
  declineLoading,
  selectLoading,
  canSelectPatient = false,
}) => {
  const [expanded, setExpanded] = useState(false);
  const [cmrDialogOpen, setCmrDialogOpen] = useState(false);
  const [phoneVisible, setPhoneVisible] = useState(false);

  const isSMSCheckIn = visit.checkInMethod === 'sms';
  const isTargetedInvite = visit.checkInMethod === 'targeted_invite';
  const isQRCheckIn = visit.checkInMethod === 'qr_code';
  const hasFormSnapshotCheckIn = isQRCheckIn || isTargetedInvite;

  // Get patient display name
  const getPatientName = () => {
    if (hasFormSnapshotCheckIn) {
      const cmr = visit.cmrSnapshot || {};
      return cmr['patient.fullName'] || 
             `${cmr['patient.firstName'] || ''} ${cmr['patient.lastName'] || ''}`.trim() ||
             'Unknown Patient';
    }
    return `Patient #${visit.patientNumber || '?'}`;
  };

  // Get phone number (masked or full)
  const getPhoneDisplay = () => {
    const phone = visit.phone || visit.cmrSnapshot?.['patient.phone'];
    if (!phone) return null;
    if (phoneVisible) return phone;
    // Mask middle digits
    return phone.replace(/(\+?\d{2})\d{6}(\d{4})/, '$1******$2');
  };

  const checkedInTime = visit.checkedInAt instanceof Date 
    ? visit.checkedInAt 
    : visit.checkedInAt?.toDate?.() || new Date(visit.checkedInAt);

  const isDeclined = visit.status === 'declined';

  const checkInTypeMeta = (() => {
    if (isQRCheckIn) {
      return {
        tooltip: 'QR code check-in',
        icon: <QrCode2 color="primary" />,
        bannerBg: 'primary.50',
        borderColor: 'primary.main',
      };
    }
    if (isTargetedInvite) {
      return {
        tooltip: 'Remote check-in',
        icon: <PhonelinkRing color="info" />,
        bannerBg: 'info.50',
        borderColor: 'info.main',
      };
    }
    return {
      tooltip: 'SMS check-in',
      icon: <Sms color="secondary" />,
      bannerBg: 'secondary.50',
      borderColor: 'secondary.main',
    };
  })();

  return (
    <>
      <Paper
        elevation={1}
        sx={{
          p: 2,
          mb: 2,
          borderLeft: 4,
          borderColor: isDeclined ? tokens.colors.msiRed.light : checkInTypeMeta.borderColor,
          opacity: isDeclined ? 0.75 : 1,
          '&:hover': { bgcolor: 'action.hover' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {/* Check-in Type Icon */}
          <Tooltip title={checkInTypeMeta.tooltip}>
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'center',
              width: 40,
              height: 40,
              borderRadius: 1,
              bgcolor: checkInTypeMeta.bannerBg,
            }}>
              {checkInTypeMeta.icon}
            </Box>
          </Tooltip>

          {/* Patient Info */}
          <Box sx={{ flex: 1 }}>
            <Stack direction="row" spacing={2} alignItems="center" flexWrap="wrap">
              <PatientBanner
                density="compact"
                patient={
                  hasFormSnapshotCheckIn
                    ? (() => {
                        const cmr = visit.cmrSnapshot || {};
                        const allergiesRaw = cmr['allergies.all'];
                        const allergies =
                          Array.isArray(allergiesRaw) && allergiesRaw.length > 0 ? allergiesRaw : undefined;
                        const akaList = (visit.akaNames || [])
                          .map((a) => `${a.firstName || ''} ${a.lastName || ''}`.trim())
                          .filter(Boolean);
                        return {
                          name: getPatientName(),
                          dob: formatDateDisplay(cmr['patient.dateOfBirth']),
                          mrn: cmr['patient.mrn'] || cmr['identifiers.mrn'] || undefined,
                          aka: akaList.length > 0 ? akaList : undefined,
                          allergies,
                          phone: cmr['patient.phone'] || undefined,
                        };
                      })()
                    : { name: getPatientName() }
                }
              />
              {showClinicName && clinicName && (
                <Chip 
                  label={clinicName} 
                  size="small" 
                  variant="outlined" 
                  color="secondary"
                  sx={{ height: 22 }}
                />
              )}
              <Tooltip title="Check-in time">
                <Typography variant="body2" color="text.secondary">
                  {format(checkedInTime, 'h:mm a')}
                </Typography>
              </Tooltip>
            </Stack>

            {/* Phone number for SMS check-ins */}
            {isSMSCheckIn && (
              <Stack direction="row" spacing={1} alignItems="center" sx={{ mt: 0.5 }}>
                <Phone sx={{ fontSize: '0.9rem', color: 'text.secondary' }} />
                <Typography 
                  variant="body2" 
                  color="text.secondary"
                  onClick={() => setPhoneVisible(!phoneVisible)}
                  sx={{ cursor: 'pointer', '&:hover': { textDecoration: 'underline' } }}
                >
                  {getPhoneDisplay()}
                </Typography>
              </Stack>
            )}

            {/* CMR Summary for QR / targeted check-ins (full form snapshot) */}
            {hasFormSnapshotCheckIn && (
              <CMRSummary 
                cmrSnapshot={visit.cmrSnapshot} 
                customFieldResponses={visit.customFieldResponses}
              />
            )}
          </Box>

          {/* Status */}
          <StatusChip 
            status={visit.status} 
            calledAt={visit.calledAt} 
            visit={visit}
            onResumeSession={onResumeSession}
          />

          {/* Actions */}
          <Stack direction="row" spacing={1}>
            {/* View CMR (form snapshot check-ins) */}
            {hasFormSnapshotCheckIn && (
              <Tooltip title="View CMR">
                <IconButton size="small" onClick={() => setCmrDialogOpen(true)}>
                  <Visibility fontSize="small" />
                </IconButton>
              </Tooltip>
            )}

            {/* Select Patient - for professional-chartmind to start ChartMind session */}
            {canSelectPatient && (visit.status === 'checked_in' || visit.status === 'called') && (
              <LoadingButton
                variant="contained"
                color="primary"
                size="small"
                loading={selectLoading}
                onClick={() => onSelectPatient(visit)}
                startIcon={<PlayArrow />}
              >
                Select Patient
              </LoadingButton>
            )}

            {/* Notify */}
            {visit.status === 'checked_in' && (
              <LoadingButton
                variant="contained"
                size="small"
                loading={notifyLoading}
                onClick={() => onNotify(visit)}
                startIcon={<NotificationsActive />}
              >
                Notify
              </LoadingButton>
            )}

            {/* Complete */}
            {(visit.status === 'called' || visit.status === 'in_progress') && (
              <LoadingButton
                variant="contained"
                color="success"
                size="small"
                loading={updateLoading}
                onClick={() => onUpdateStatus(visit, 'completed')}
                startIcon={<CheckCircle />}
              >
                Complete
              </LoadingButton>
            )}

            {/* Decline - available for active (non-completed, non-declined) visits */}
            {!isDeclined && visit.status !== 'completed' && visit.status !== 'discharged' && onDecline && (
              <LoadingButton
                variant="outlined"
                size="small"
                loading={declineLoading}
                onClick={() => onDecline(visit)}
                startIcon={<DoNotDisturb />}
                sx={{ 
                  color: tokens.colors.msiRed.light, 
                  borderColor: tokens.colors.msiRed.light,
                  '&:hover': {
                    bgcolor: alpha(tokens.colors.msiRed.light, 0.08),
                    borderColor: tokens.colors.msiRedHoverBorder.light,
                  },
                }}
              >
                Decline
              </LoadingButton>
            )}

            {/* Delete */}
            <Tooltip title="Remove from queue">
              <span>
                <IconButton
                  size="small"
                  color="error"
                  onClick={() => onDelete(visit)}
                  disabled={deleteLoading}
                >
                  <Delete fontSize="small" />
                </IconButton>
              </span>
            </Tooltip>
          </Stack>
        </Box>

        {/* Decline reason banner (shown after declining) */}
        {isDeclined && visit.statusMessage && (
          <Alert 
            severity="error" 
            icon={<DoNotDisturb />}
            sx={{
              mt: 1.5,
              bgcolor: alpha(tokens.colors.msiRed.light, 0.08),
              color: tokens.colors.msiRed.light,
              '& .MuiAlert-icon': { color: tokens.colors.msiRed.light },
            }}
          >
            <Typography variant="caption" fontWeight="bold">Decline reason:</Typography>
            <Typography variant="body2">{visit.statusMessage}</Typography>
          </Alert>
        )}
      </Paper>

      {/* CMR Dialog */}
      <CMRDialog 
        open={cmrDialogOpen} 
        onClose={() => setCmrDialogOpen(false)} 
        visit={visit}
      />
    </>
  );
};

export default UnifiedQueuePatient;
